import { useEffect, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InboxLocationPhones, InboxLocationPhonesTypes } from '@frontend/inbox-location-phones';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import {
  PopoverMenu,
  PopoverMenuItem,
  SearchField,
  TextButton,
  useFormField,
  Text,
  Chip,
  useAlert,
} from '@frontend/design-system';
import { useInboxFiltersShallowStore } from '../../../stores/inbox-store';
import { FilterItemPopoverCheckboxField } from '../filter-item-popover-checkbox-field';
import { GetItemPropsFn, GetMenuPropsFn } from '../types';

const removeNonAlphanumericChars = (str: string): string => {
  return str.replace(/[^A-Za-z0-9]/g, '');
};

type LocationPhonesFilterPopoverMenuProps = {
  getMenuProps: GetMenuPropsFn;
  getItemProps: GetItemPropsFn;
  toggleItems: (locationPhones: string[]) => void;
  selectedLocationPhones: string[];
};

export const LocationPhonesFilterPopoverMenu = ({
  getMenuProps,
  getItemProps,
  toggleItems,
  selectedLocationPhones,
}: LocationPhonesFilterPopoverMenuProps) => {
  const { t } = useTranslation('inbox');
  const { getScopeName } = useAppScopeStore();
  const alert = useAlert();
  const { inboxFilters, setInboxFilters } = useInboxFiltersShallowStore('setInboxFilters');
  const searchField = useFormField({
    type: 'text',
  });
  const {
    data: { locationPhonesByLocation, allLocationPhones },
    checkIsLocationPhoneAiEnabled,
  } = InboxLocationPhones.Hooks.useLocationPhones();
  const filteredLocationPhones = useMemo<
    { locationId: string; locationPhones: InboxLocationPhonesTypes.LocationPhone[] }[]
  >(() => {
    const searchValue = removeNonAlphanumericChars(searchField.value?.toLowerCase() ?? '');
    return Object.entries(locationPhonesByLocation).reduce<
      { locationId: string; locationPhones: InboxLocationPhonesTypes.LocationPhone[] }[]
    >((acc, [locationId, locationPhones]) => {
      const filteredLocationPhones = searchValue
        ? locationPhones.filter(({ displayName, locationPhone }) => {
            const locationPhoneSearchValue = removeNonAlphanumericChars(
              `${displayName ?? ''}${locationPhone}`.trim().toLowerCase()
            );

            return locationPhoneSearchValue.includes(searchValue);
          })
        : locationPhones;
      const sortedLocationPhones = [...filteredLocationPhones].sort(InboxLocationPhones.Utils.sortLocationPhones);
      if (sortedLocationPhones.length) {
        acc.push({ locationId, locationPhones: sortedLocationPhones });
      }
      return acc;
    }, []);
  }, [locationPhonesByLocation, searchField.value]);
  const allLocationPhonesString = useMemo(() => JSON.stringify(locationPhonesByLocation), [locationPhonesByLocation]);

  const handleSelectAll = () => {
    const locationPhones = filteredLocationPhones.reduce<string[]>((acc, { locationPhones }) => {
      acc.push(
        ...locationPhones.map((entry) => entry.locationPhone).filter((phone) => !selectedLocationPhones.includes(phone))
      );
      return acc;
    }, []);
    toggleItems(locationPhones);
  };

  const handleClear = () => {
    const locationsToDeselect = filteredLocationPhones.reduce<string[]>((acc, { locationPhones }) => {
      acc.push(
        ...locationPhones.reduce<string[]>((acc, entry) => {
          if (selectedLocationPhones.includes(entry.locationPhone ?? '')) {
            acc.push(entry.locationPhone);
          }
          return acc;
        }, [])
      );
      return acc;
    }, []);
    toggleItems(locationsToDeselect);
  };

  const hasAllSelected = filteredLocationPhones?.every(({ locationPhones }) =>
    locationPhones.every(({ locationPhone }) => selectedLocationPhones.includes(locationPhone ?? ''))
  );
  const hasNoneSelected = filteredLocationPhones?.every(({ locationPhones }) =>
    locationPhones.every(({ locationPhone }) => !selectedLocationPhones.includes(locationPhone ?? ''))
  );

  useEffect(() => {
    const removedSelectedLocationPhones = selectedLocationPhones.filter(
      (phoneNumber) => !allLocationPhones.some(({ locationPhone }) => locationPhone === phoneNumber)
    );
    if (removedSelectedLocationPhones.length) {
      toggleItems(removedSelectedLocationPhones);
      setInboxFilters({
        ...inboxFilters,
        locationPhones: selectedLocationPhones.filter(
          (phoneNumber) => !removedSelectedLocationPhones.includes(phoneNumber)
        ),
      });
      alert.info(
        t('{{count}} departments have been removed from your filters, because they are no longer applicable.', {
          count: removedSelectedLocationPhones.length,
        })
      );
    }
  }, [allLocationPhonesString]);

  return (
    <PopoverMenu {...getMenuProps()}>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 0,
          disableCloseOnSelect: true,
          onFocusCapture: (e) => {
            e.preventDefault();
            e.stopPropagation();
            searchField.onFocus();
          },
          onBlurCapture: (e) => {
            e.preventDefault();
            e.stopPropagation();
            searchField.onBlur();
          },
        })}
        css={{
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
          // make the search field container take the full width of the popover
          '& > div': {
            width: '100%',
          },
        }}
      >
        <SearchField
          {...searchField}
          name='department-search'
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
        />
      </PopoverMenuItem>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 1,
          disableCloseOnSelect: true,
        })}
        css={{
          marginTop: theme.spacing(1),
          padding: theme.spacing(1, 0.75),
          borderBottom: `1px solid ${theme.colors.neutral20}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <TextButton
          css={{
            color: theme.font.colors.primary,
            fontWeight: theme.font.weight.semibold,
          }}
          onClick={handleSelectAll}
          disabled={hasAllSelected}
          trackingId={`${InboxPrefixes.List}-filters-popover-departments-select-all`}
        >
          {t('Select All')}
        </TextButton>
        <TextButton
          css={{
            color: theme.font.colors.primary,
            fontWeight: theme.font.weight.semibold,
          }}
          onClick={handleClear}
          disabled={hasNoneSelected}
          trackingId={`${InboxPrefixes.List}-filters-popover-departments-clear`}
        >
          {t('Clear')}
        </TextButton>
      </PopoverMenuItem>
      <div
        css={{
          maxHeight: 240,
          overflowY: 'auto',
        }}
      >
        {filteredLocationPhones?.length ? (
          filteredLocationPhones.map(({ locationId, locationPhones }) => {
            return (
              <>
                {Object.keys(locationPhonesByLocation).length > 1 && (
                  <div css={{ display: 'flex', alignItems: 'center', padding: theme.spacing(1, 2) }}>
                    <Icon name='location' css={{ marginRight: theme.spacing(1) }} />
                    <Text css={{ fontWeight: 'bold' }}>{getScopeName(locationId)}</Text>
                  </div>
                )}
                {locationPhones.map(({ defaultSms, displayName, locationPhone }, index) => {
                  if (!locationPhone && !displayName) return null;

                  return (
                    <PopoverMenuItem
                      key={locationPhone}
                      {...getItemProps({
                        index: index + 2,
                        disableCloseOnSelect: true,
                        onClickCapture: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleItems([locationPhone]);
                        },
                      })}
                      css={{
                        width: '100%',
                        cursor: 'pointer',
                        '*': {
                          cursor: 'pointer',
                        },
                        label: {
                          width: '100%',
                        },
                      }}
                      trackingId={`${InboxPrefixes.List}-filters-popover-location-phones-select-location-phone`}
                    >
                      <FilterItemPopoverCheckboxField
                        isChecked={selectedLocationPhones.includes(locationPhone)}
                        label={
                          <div
                            css={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 8,
                              width: '100%',
                            }}
                          >
                            <Chip
                              css={{ fill: theme.colors.neutral50, maxWidth: 150 }}
                              leftElement={<Icon name='fax-number' size={16} />}
                              variant='default'
                              tooltipLabel={formatPhoneNumber(locationPhone)}
                            >
                              {InboxLocationPhones.Utils.getLocationPhoneDisplayName({ locationPhone, displayName })}
                            </Chip>
                            <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.25) }}>
                              {checkIsLocationPhoneAiEnabled(locationPhone) && (
                                <Chip
                                  variant='interactive'
                                  tooltipLabel={t('AI Receptionist enabled number')}
                                  css={{
                                    padding: theme.spacing(0.5),
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing(0.5),
                                  }}
                                >
                                  <Icon
                                    name='aiweave-assistant'
                                    height={16}
                                    width={16}
                                    css={{ minWidth: theme.spacing(2) }}
                                  />
                                </Chip>
                              )}
                              {defaultSms && (
                                <Chip variant='primary' tooltipLabel={t('Default SMS Number')} css={{ padding: 4 }}>
                                  <Icon
                                    name='location-default'
                                    height={16}
                                    width={16}
                                    css={{ minWidth: theme.spacing(2) }}
                                  />
                                </Chip>
                              )}
                            </div>
                          </div>
                        }
                        name={displayName || formatPhoneNumber(locationPhone, true)}
                      />
                    </PopoverMenuItem>
                  );
                })}
              </>
            );
          })
        ) : (
          <Text
            size='medium'
            textAlign='center'
            color='light'
            css={{
              padding: theme.spacing(1),
            }}
          >
            {t('No departments to display.')}
          </Text>
        )}
      </div>
    </PopoverMenu>
  );
};
